import { cn } from '@/lib/cn';
import { OPERATION_KIND, type OperationKind } from './types';

interface KindCard {
  kind: OperationKind;
  glyph: string;
  title: string;
  blurb: string;
  titleClass: string;
  ringClass: string;
}

const CARDS: KindCard[] = [
  {
    kind: OPERATION_KIND.INCOMING,
    glyph: '↓',
    title: 'Incoming',
    blurb: 'Wire received from a third party or own account',
    titleClass: 'text-[#2e7d32]',
    ringClass: 'border-[#2e7d32] ring-1 ring-[#2e7d32]',
  },
  {
    kind: OPERATION_KIND.CONVERSION,
    glyph: '⇄',
    title: 'Conversion',
    blurb: "Internal FX between two of the client's currencies",
    titleClass: 'text-[#6a1b9a]',
    ringClass: 'border-[#6a1b9a] ring-1 ring-[#6a1b9a]',
  },
  {
    kind: OPERATION_KIND.PAYMENT,
    glyph: '↑',
    title: 'Payment',
    blurb: 'Outgoing wire to a beneficiary or own account',
    titleClass: 'text-[#b71c1c]',
    ringClass: 'border-[#b71c1c] ring-1 ring-[#b71c1c]',
  },
];

export interface OperationKindCardsProps {
  value: OperationKind;
  onChange: (kind: OperationKind) => void;
  /** Kinds whose form has not shipped yet — rendered, but not selectable. */
  unavailable?: OperationKind[];
}

export function OperationKindCards({ value, onChange, unavailable = [] }: OperationKindCardsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" role="radiogroup" aria-label="Operation">
      {CARDS.map((card) => {
        const isSelected = card.kind === value;
        const isDisabled = unavailable.includes(card.kind);

        return (
          <button
            key={card.kind}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={isDisabled}
            title={isDisabled ? 'Not available yet' : undefined}
            onClick={() => onChange(card.kind)}
            className={cn(
              'rounded-lg border bg-white p-4 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6B0F1A] focus-visible:ring-offset-2',
              isSelected ? card.ringClass : 'border-[#e0e0e0]',
              isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:border-[#bbb]',
            )}
            data-testid={`operation-kind-${card.kind}`}
          >
            <span aria-hidden="true" className="block text-lg leading-none text-[#333]">
              {card.glyph}
            </span>
            <span className={cn('mt-3 block text-sm font-semibold', card.titleClass)}>
              {card.title}
            </span>
            <span className="mt-1 block text-xs text-[#999]">{card.blurb}</span>
          </button>
        );
      })}
    </div>
  );
}
